import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  createStyles,
  FileButton,
  Flex,
  Group,
  Loader,
  Skeleton,
  Text,
} from "@mantine/core";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import { useSelector } from "react-redux";
import { MdKeyboardArrowLeft } from "react-icons/md";
import { useForm } from "@mantine/form";
import {
  decrypt,
  encrypt,
  getAbbreviationFromName,
  showError,
  showSuccess,
} from "../../../utils";

import { uploadStaffProfile } from "../../../services/staff";
import { addFacility, editFacility, getAllFacilities } from "../../../services";
import TableLoader from "../../../components/TableLoader";
import { FacilityDetailsTab } from "../../../components/FacilityDetails/FacilityDetailsTab";
import { ProviderTab } from "../../../components/FacilityDetails/ProviderTab";
import { StaffDocumentsTab } from "../../../components/StaffDetails/StaffDocumentsTab";
import { FacilitySettingsTab } from "../../../components/FacilityDetails/FacilitySettingsTab";
import UploadStaffDoc from "../../../components/StaffDetails/UploadStaffDoc";
import { uploadFileToS3Api } from "../../../utils/PresignedUrlFunction";

const useStyles = createStyles((theme) => ({
  content: {
    display: "flex",
    width: "100%",
    height: "100%",
    minHeight: "100vh",
    flexDirection: "column",
    overflowX: "auto",
    backgroundColor: "#F5F5F5",
  },
  staffInfoContainer: {
    borderRadius: "10px",
    backgroundColor: "#FCFCFC",
    margin: "20px",
  },
  staffDetails: {
    display: "flex",
    alignItems: "center",
    padding: "20px",
    gap: "20px",
  },
  tabContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "fit-content",
    gap: "20px",
    padding: "0 20px",
  },
  tab: {
    padding: "10px 0",
    cursor: "pointer",
    borderBottom: "2px solid transparent",
    transition: "all 0.2s ease",
    color: "#262626",
    fontFamily: "Inter",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    textTransform: "capitalize",
  },
  activeTab: {
    fontWeight: 700,
    color: "#000",
    borderBottom: "2px solid #000",
  },
  disabledTab: {
    pointerEvents: "none",
    opacity: 0.5,
    cursor: "not-allowed",
    color: "#262626",
  },
  title: {
    margin: "0 20px",
    color: "#171717",
    fontFamily: "Inter",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    marginTop: "15px",
    textTransform: "capitalize",
  },
}));
const FacilityDetails = () => {
  const { id, mode } = useParams();
  const { facilities, loading } = useSelector((state) => state.facility);

  const facilitySelected = facilities?.facilities?.find(
    (s) => String(s?.id) === String(id)
  );

  const { profile } = useSelector((state) => state?.profile);
  const { classes, cx } = useStyles();
  const navigate = useNavigate();

  const tabs = ["details", "provider", "documents", "settings"];
  const [activeTab, setActiveTab] = useState("details");
  const openRef = useRef();
  const [image, setImage] = useState(null);
  const [imageloading, setImageLoading] = useState(false);
  const [returnedFacility, setReturnedId] = useState();

  const edit = facilitySelected ? true : false;

  const location = useLocation();
  let notificationDoc = location.state?.notificationDoc;

  useEffect(() => {
    getAllFacilities();
  }, []);

  useEffect(() => {
    setImage(facilitySelected?.photo);
  }, [facilitySelected]);

  useEffect(() => {
    if (notificationDoc) {
      setActiveTab("documents");
    }
  }, [notificationDoc, location]);

  const form = useForm({
    initialValues: {
      name: edit ? facilitySelected?.name : "",
      location: edit ? facilitySelected?.addressLineTwo : "",
      address: edit ? facilitySelected?.address : "",
      zip: edit ? facilitySelected?.zipcode : "",
      state: edit ? facilitySelected?.state : null,
      phone: edit ? facilitySelected?.phone : "",
      email: edit ? facilitySelected?.email : "",
      providerLicense: edit ? facilitySelected?.providerLicense || '' : "",
    },
    validate: {
      name: (value) => (value ? null : "Required"),
      location: (value) => (value ? null : "Required"),
      address: (value) => (value ? null : "Required"),
      zip: (value) => (value ? null : "Required"),
      state: (value) => (value ? null : "Required"),
      phone: (value) => (value ? null : "Required"),
      email: (value) => (value ? null : "Required"),
      providerLicense: (value) => (value ? null : "Required"),
    },
  });

  const settingsForm = useForm({
    initialValues: {
      type: edit ? facilitySelected?.type?.id : null,
      checkInType: edit ? facilitySelected?.checkInType : null,
      evvRequired: edit ? facilitySelected?.evvRequired : "",
      timeZone: edit ? facilitySelected?.timeZone : null,
      skaiAccess: edit ? facilitySelected?.skaiAccess === false ? false : true : true,
    },
    validate: {
      type: (value) => (value ? null : "Required"),
      checkInType: (value) => (value ? null : "Required"),
      evvRequired: (value) => (value ? null : "Required"),
      timeZone: (value) => (value ? null : "Required"),
      skaiAccess: (value) => (value === false || value === true ? null : "Required"),
    },
  });

  // const [addedFacilities, setAddedFacilities] = useState(
  //   facilitySelected?.facilityId?.map((f) => ({
  //     value: f.id,
  //     label: f.name,
  //   })) || []
  // );

  // const [role, setRole] = useState("");
  // const [staffResponse, setStaffResponse] = useState();
  // const [roleError, setRoleError] = useState(false);

  useEffect(() => {
    getAllFacilities(1);
  }, [1]);

  const selectedStateAbbr = getAbbreviationFromName(form?.values?.state);

  useEffect(() => {
    if (selectedStateAbbr) {
      form.setFieldValue("state", selectedStateAbbr);
    }
  }, [selectedStateAbbr]);

  const handleSave = async (values) => {
    const payload = { ...form.values, zipcode: values?.zip, photo: image };
    // setActiveTab("provider");
    if (edit) {
      editFacility({ ...payload, id: facilitySelected?.id });
    } else {
      let response = await addFacility(payload);
      setReturnedId(response.facility);
    }
    setActiveTab("provider");
    // form?.reset();
  };

  const handleUpdateSetting = async () => {
    const payload = { ...settingsForm.values, photo: image };
    if (facilitySelected) {
      payload.id = facilitySelected?.id;
    } else {
      payload.id = returnedFacility?.id;
    }
    editFacility({ ...payload });

    navigate("/home/facility");
  };

  const handleCancel = () => {
    navigate("/home/facility");
    form.reset();
  };

  //   Function to handle image upload and processing
  // const onChangePhoto2 = async (files) => {
  //   setImageLoading(true);
  //   if (files) {
  //     const formData = new FormData();
  //     formData.append("file", files);
  //     const localImageUrl = await uploadStaffProfile(formData); // Generate blob URL
  //     const encryptedImageUrl = encrypt(localImageUrl?.imageUrl); // If needed
  //     setImage(encryptedImageUrl);
  //     setImageLoading(false);
  //   }
  //   if (openRef.current) {
  //     openRef.current();
  //   }
  // };

  const onChangePhoto = async (files) => {
    if (!files) return;
    try {
      setImageLoading(true);

      // 1. Upload to S3
      const result = await uploadFileToS3Api({ file: files });

      // 2. Encrypt the image URL (if needed)
      const encryptedImageUrl = encrypt(result?.uploadURL);

      // 3. Update UI with image
      setImage(encryptedImageUrl);

      // 4. Trigger modal or UI action
      if (openRef.current) {
        openRef.current();
      }
    } catch (error) {
      console.error("Upload failed", error);
      showError(error?.message || "Upload failed");
    } finally {
      setImageLoading(false);
    }
  };

  return (
    <Box fluid className={classes.content}>
      <AppNavbar title={`Facility`} profile={profile} />
      <Flex
        align="center"
        gap={5}
        m={10}
        onClick={() => navigate("/home/facility")}
        style={{ cursor: "pointer", width: "fit-content" }}
      >
        <MdKeyboardArrowLeft size={30} />
        <Text fw={400} fz={12} c={"#262626"} ff={"Inter"}>
          Back
        </Text>
      </Flex>
      <Box className={classes.staffInfoContainer}>
        {loading ? (
          <Skeleton
            height={60}
            radius="sm"
            style={{ margin: "10px", width: "200px" }}
          />
        ) : (
          <Box className={classes.staffDetails}>
            {imageloading ? (
              <Loader size="md" radius="100%" color="yellow" />
            ) : (
              <Avatar
                src={decrypt(image)}
                size={50}
                radius={50}
                shape="circle"
              />
            )}
            {mode !== "view" && (
              <Group>
                <FileButton
                  onChange={(files) => onChangePhoto(files)}
                  openRef={openRef}
                  accept="image/*"
                >
                  {(props) => (
                    <Button
                      variant="filled"
                      color="gray"
                      size="xs"
                      radius="md"
                      {...props}
                    >
                      {edit ? "Change Photo" : "Add Photo"}
                    </Button>
                  )}
                </FileButton>
              </Group>
            )}
            {mode !== "add" && (
              <Box className={classes.staffInfo}>
                <Text variant="text" c={"#737373"} fw={400} fz={12}>
                  Facility name
                </Text>
                <Text variant="text" fz={16} fw={600} ff={"Inter"}>
                  {facilitySelected?.name || "-"}
                </Text>
                <Text
                  variant="text"
                  fz={14}
                  fw={500}
                  c={"#737373"}
                  ff={"Inter"}
                  transform={"uppercase"}
                >
                  {facilitySelected?.id?.slice(0, 6)}
                </Text>
              </Box>
            )}
          </Box>
        )}

        {/* Custom Tabs */}
        <Box className={classes.tabContainer}>
          {tabs?.map((tab, index) => {
            const isTabDisabled =
              mode === "add" && !returnedFacility && tab !== "details";

            return (
              <Text
                key={tab}
                className={cx(classes.tab, {
                  [classes.activeTab]: activeTab === tab,
                  [classes.disabledTab]: isTabDisabled,
                })}
                onClick={() => {
                  if (!isTabDisabled) setActiveTab(tab);
                }}
              >
                {tab}
              </Text>
            );
          })}
        </Box>
      </Box>
      {activeTab !== "provider" && activeTab !== "documents" && (
        <Text className={classes.title}>{`${mode} Facility`}</Text>
      )}

      {/* Tab Content */}
      <Box
        className={activeTab !== "provider" ? classes.staffInfoContainer : ""}
      >
        {loading ? (
          <TableLoader />
        ) : (
          <Box px={20} pb={20}>
            {activeTab === "details" && (
              <FacilityDetailsTab
                mode={mode}
                form={form}
                handleSave={handleSave}
                activeTab={activeTab}
                handleCancel={handleCancel}
                loading={loading}
                data={facilitySelected}
                selectedStateAbbr={selectedStateAbbr}
              />
            )}
            {activeTab === "provider" && (
              <ProviderTab
                returnedFacility={facilitySelected?.id || returnedFacility}
                setActiveTab={setActiveTab}
                mode={mode}
              />
            )}
            {activeTab === "documents" && (
              <StaffDocumentsTab
                userId={facilitySelected?.id || returnedFacility?.id}
                userType="facility"
              />
            )}
            {activeTab === "settings" && (
              <FacilitySettingsTab
                data={facilitySelected}
                mode={mode}
                form={settingsForm}
                handleSave={handleUpdateSetting}
                handleCancel={handleCancel}
                staffResponse={returnedFacility}
                // activeTab={activeTab}
              />
            )}{" "}
          </Box>
        )}
      </Box>

      {activeTab === "documents" && notificationDoc ? (
        <UploadStaffDoc
          selectedFolder={notificationDoc?.metadata?.folder}
          userId={notificationDoc?.facility?.id}
          userType={notificationDoc?.userType}
          isDocument={true}
          uploadedFiles={notificationDoc?.metadata?.file}
          isnotificationdoc={true}
        />
      ) : null}
    </Box>
  );
};

export default FacilityDetails;
